import R from 'ramda';
import objectRecord from '../reducers/object_record';
import { createSelector } from 'reselect';
import { isInstructorInSection } from './current_user';
import {
    selectTerm,
    allTerms as selectAllTerms,
    selectCurrentTerm,
} from './terms';
import { selectContentForSection } from './content';
import deprecated from '../deprecated';

import { param } from './utils';
import { SectionRecord } from 'reducers/section';

const selectRoot = root => root;

const selectSectionRoot = root => root.section;

const numbers = /[\d.]+/;
function lexicalSorter(a, b) {
    // Users never seem to expect case to matter when sorting.
    const aName = a.toLowerCase();
    const bName = b.toLowerCase();

    // eslint-disable-next-line
    if (aName == bName) {
        return 0;
    }
    // If the only difference between the two is a number, then
    // compare the numbers.
    // eslint-disable-next-line
    if (aName.replace(numbers, '') == bName.replace(numbers, '')) {
        const aNum = parseFloat(aName.match(numbers));
        const bNum = parseFloat(bName.match(numbers));
        // identical names are handled above, so this never needs to
        // return 0
        return aNum < bNum ? -1 : 1;
    }
    if (aName < bName) {
        return -1;
    }
    return 1;
}

const sectionSorter = root => (a, b) => {
    const termA = selectTerm(root, a.term_id);
    const termB = selectTerm(root, b.term_id);

    return (
        // The `||`s will cause any 0s to fall through. If the dates
        // match it will check the subect, if they also match it will
        // check the catalog_number
        new Date(termB.start_date) - new Date(termA.start_date) ||
        lexicalSorter(a.course_subject, b.course_subject) ||
        lexicalSorter(a.class_number, b.class_number) ||
        a.id - b.id // last resort, sort by ID
    );
};

const contentCollection = objectRecord({
    name: '',
    section_id: null,
    content: [],
});

const selectAllSections = createSelector(
    selectSectionRoot,
    R.values
);

export const getAllSections = createSelector(
    selectRoot,
    selectAllSections,
    (root, sections) => R.sort(sectionSorter(root), sections)
);

export const selectInstructorSections = createSelector(
    selectRoot,
    getAllSections,
    (root, sections) => sections.filter(s => isInstructorInSection(root, s))
);

//export const selectInstructorSections = createSelector(
export const selectInstructorSectionsTerms = createSelector(
    selectAllTerms,
    selectInstructorSections,
    (terms, sections) => {
        const instructorSectionIds = R.compose(
            R.uniq,
            R.map(R.prop('id'))
        )(sections);

        const isInstructorInSection = R.contains(R.__, instructorSectionIds);
        return R.filter(term => {
            return R.any(isInstructorInSection, term.section_ids);
        }, terms);
    }
);

// (root, id) => section
export const selectSection = createSelector(
    selectSectionRoot,
    param(1),
    (state, id) => {
        return state[id] || new SectionRecord();
    }
);

export const getSection = deprecated(
    selectSection,
    `Use selectSection instead`
);

export const selectCurrentTermSections = createSelector(
    [selectCurrentTerm, selectSectionRoot],
    ({ section_ids = [] }, root) => {
        return section_ids.map(id => root[id]).filter(t => t);
    }
);

// (root, id) => boolean
export const hasSection = createSelector(
    selectSection,
    section => section != null
);

// (root, section) => [ ...collections ]
export const selectCollections = createSelector(
    param(1),
    (root, section) => selectContentForSection(root, section),
    (section, content) => {
        let collections = section.tags.reduce((cols, name) => {
            cols[name] = contentCollection({
                name,
                section_id: section.id,
            });
            return cols;
        }, {});

        content.forEach(content => {
            content.tags.forEach(tag => {
                if (collections[tag] != null) {
                    collections = R.over(
                        R.lensPath([tag, 'content']),
                        R.append(content),
                        collections
                    );
                }
            });
        });

        return section.tags
            .map(name => collections[name])
            .sort((a, b) => lexicalSorter(a.name, b.name));
    }
);

export const getCollections = deprecated(
    selectCollections,
    `Use selectCollections instead`
);



// WEBPACK FOOTER //
// ./src/selectors/section.js